import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Crown, Calendar, Users, CreditCard, ArrowUp, ArrowDown, 
  Check, AlertTriangle, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MySubscriptionDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (open) {
      fetchSubscription();
      fetchPlans();
    }
  }, [open]);

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/my-subscription`, { 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/select-plan');
  };

  const handleRenew = async () => {
    if (subscription?.plan_id) {
      onOpenChange(false);
      navigate(`/checkout/${subscription.plan_id}`);
    }
  };

  const getStatusBadge = () => {
    if (!subscription) return null;
    
    const daysLeft = subscription.valid_until 
      ? differenceInDays(new Date(subscription.valid_until), new Date())
      : 0;

    if (subscription.status === 'active' && daysLeft > 7) {
      return <Badge className="bg-green-500">Active</Badge>;
    } else if (subscription.status === 'active' && daysLeft <= 7 && daysLeft > 0) {
      return <Badge className="bg-amber-500">Expiring Soon</Badge>;
    } else if (subscription.status === 'expired' || daysLeft <= 0) {
      return <Badge variant="destructive">Expired</Badge>;
    } else if (subscription.status === 'cancelled') {
      return <Badge variant="secondary">Cancelled</Badge>;
    }
    return <Badge variant="outline">{subscription.status}</Badge>;
  };

  const getDaysRemaining = () => {
    if (!subscription?.valid_until) return null;
    const days = differenceInDays(new Date(subscription.valid_until), new Date());
    if (days < 0) return 'Expired';
    if (days === 0) return 'Expires today';
    if (days === 1) return '1 day remaining';
    return `${days} days remaining`;
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-yellow-500" />
            My Subscription
          </DialogTitle>
          <DialogDescription>
            View and manage your subscription plan
          </DialogDescription>
        </DialogHeader>

        {subscription ? (
          <div className="space-y-4">
            {/* Current Plan */}
            <div className="p-4 rounded-lg border bg-muted/30">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-lg">{subscription.plan_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {subscription.is_free ? 'Free Trial' : `₹${subscription.price}/${subscription.billing_cycle}`}
                  </p>
                </div>
                {getStatusBadge()}
              </div>
              
              {/* Validity */}
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>
                  Valid until: {subscription.valid_until 
                    ? format(new Date(subscription.valid_until), 'PPP')
                    : 'N/A'}
                </span>
              </div>
              
              {getDaysRemaining() && (
                <div className={`mt-1 text-sm font-medium ${
                  getDaysRemaining().includes('Expired') || getDaysRemaining().includes('today')
                    ? 'text-red-500'
                    : getDaysRemaining().includes('remaining') && parseInt(getDaysRemaining()) <= 7
                      ? 'text-amber-500'
                      : 'text-green-500'
                }`}>
                  {getDaysRemaining()}
                </div>
              )}
            </div>

            {/* Plan Limits */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  Users
                </div>
                <div className="font-semibold">
                  {subscription.current_users || 1} / {subscription.max_users === -1 ? '∞' : subscription.max_users}
                </div>
              </div>
              <div className="p-3 rounded-lg border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="w-4 h-4" />
                  Clients
                </div>
                <div className="font-semibold">
                  {subscription.current_clients || 0} / {subscription.max_clients === -1 ? '∞' : subscription.max_clients}
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="p-3 rounded-lg border">
              <h4 className="font-medium text-sm mb-2">Plan Features</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-1">
                  {subscription.feature_reference_commission ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <span className="w-3 h-3 text-muted-foreground">-</span>
                  )}
                  <span className={!subscription.feature_reference_commission ? 'text-muted-foreground' : ''}>
                    Reference Commission
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {subscription.feature_patti ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <span className="w-3 h-3 text-muted-foreground">-</span>
                  )}
                  <span className={!subscription.feature_patti ? 'text-muted-foreground' : ''}>
                    Patti
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {subscription.feature_monday_settling ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <span className="w-3 h-3 text-muted-foreground">-</span>
                  )}
                  <span className={!subscription.feature_monday_settling ? 'text-muted-foreground' : ''}>
                    Monday Settling
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {subscription.feature_mcx_fo ? (
                    <Check className="w-3 h-3 text-green-500" />
                  ) : (
                    <span className="w-3 h-3 text-muted-foreground">-</span>
                  )}
                  <span className={!subscription.feature_mcx_fo ? 'text-muted-foreground' : ''}>
                    MCX & F&O
                  </span>
                </div>
              </div>
            </div>

            {/* Billing History */}
            {subscription.last_payment && (
              <div className="p-3 rounded-lg border">
                <h4 className="font-medium text-sm mb-2">Last Payment</h4>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    {format(new Date(subscription.last_payment.date), 'PPP')}
                  </span>
                  <span className="font-medium">₹{subscription.last_payment.amount}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              {subscription.status === 'expired' || getDaysRemaining()?.includes('Expired') ? (
                <Button className="flex-1" onClick={handleRenew}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Renew Subscription
                </Button>
              ) : (
                <>
                  <Button variant="outline" className="flex-1" onClick={handleUpgrade}>
                    <ArrowUp className="w-4 h-4 mr-2" />
                    Upgrade Plan
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h3 className="font-semibold mb-2">No Active Subscription</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Choose a plan to get started with YellowGold Pro
            </p>
            <Button onClick={handleUpgrade}>
              View Plans
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MySubscriptionDialog;
